import type { JSX } from 'react';

import { SettingsMenu } from './SettingsMenu';
import { ThemeToggle } from './ThemeToggle';

interface ShellActionsProps {
  /** Displayed initial in the avatar. Defaults to "?". */
  avatarInitial?: string;
  /** Whether the user is a platform admin — passed through to SettingsMenu. */
  isAdmin?: boolean;
  /** Called when the user chooses "Sign out" in the menu. */
  onLogout?: () => void | Promise<void>;
  /** Optional unread notification count — shows a dot if >0. */
  notificationsCount?: number;
  variant?: 'desktop' | 'mobile';
}

/**
 * Right-side cluster: theme toggle + notifications bell + settings menu +
 * avatar. Order and spacing match `tmp/platform-mockups/platform-shell.html`.
 *
 * On mobile the cluster stays visible but notifications collapses (space).
 */
export function ShellActions({
  avatarInitial = '?',
  isAdmin = false,
  onLogout,
  notificationsCount = 0,
  variant = 'desktop',
}: ShellActionsProps): JSX.Element {
  const hasUnread = notificationsCount > 0;
  return (
    <div className="shell__actions">
      <ThemeToggle />

      {variant === 'desktop' && (
        <button
          type="button"
          className="shell__icon-btn shell__notifications"
          aria-label={hasUnread ? `Notifications (${notificationsCount} unread)` : 'Notifications'}
          data-has-unread={hasUnread || undefined}
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" />
            <path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" />
          </svg>
          {hasUnread && <span className="shell__notifications-dot" aria-hidden />}
        </button>
      )}

      <SettingsMenu isAdmin={isAdmin} onLogout={onLogout} variant={variant} />

      <div className="shell__avatar" aria-hidden="true">
        {avatarInitial.slice(0, 1).toUpperCase()}
      </div>
    </div>
  );
}
